/** Attached bedrock shoulders in the existing radial heightfield. Each finite
 * ledge retains native rock that the broad river incision would otherwise
 * remove. No new water table, query, mesh, random stream or floating solid. */
import type {RiverHit} from './rivers.js';
import {fallInfluence} from './riverFalls.js';

const CELL_M=28;
const clamp=(x:number):number=>Math.max(0,Math.min(1,x));
function hash(cell:number,river:number,side:number,salt:number):number {
  let n=Math.imul(cell,374761393)^Math.imul(river+17,668265263)^Math.imul(side+3,salt);
  n=Math.imul(n^(n>>>13),1274126177);return((n^(n>>>16))>>>0)/4294967295;
}
/** Heights use amplitude units; every shape dimension below is in metres.
 * Existing wet samples are byte-identical, so retained bank rock cannot split
 * a connected wet cross-section, dam the core or invent a dry channel island.
 * The closed heightfield attaches every cap to its underlying physical bank. */
export function carveRiverLedgeHeight(nativeH:number,carvedH:number,hit:RiverHit,A:number):number {
  const authority=hit as RiverHit&{canyonOwned?:boolean;poolInfluence?:number;landformsEnabled?:boolean};
  if(!(A>0)||nativeH<=carvedH||carvedH<=hit.ws||hit.dist<=hit.waterHalf+1.0||hit.dist>=hit.carveHalf
    ||hit.carveHalf-hit.waterHalf<4||hit.s<=40||authority.landformsEnabled===false||authority.canyonOwned||(authority.poolInfluence??0)>0)return carvedH;
  // Graph ownership begins exactly where this compact influence reaches zero.
  // Fade there rather than abruptly switching a whole retained cap off. The
  // full lip and receiving interval remain untouched (fallWeight===0).
  const fallWeight=hit.fall?1-fallInfluence(hit.fall,hit.s):1;
  if(fallWeight<=0)return carvedH;
  const side=(hit.cross??hit.dist)<0?-1:1,cell=Math.floor(hit.s/CELL_M),nativeM=nativeH*A,carvedM=carvedH*A,waterM=hit.ws*A;
  const dry=clamp((carvedM-waterM)/.7),dryWeight=dry*dry*(3-2*dry),source=clamp((hit.s-40)/12);
  let retained=0;
  for(let k=cell-1;k<=cell+1;k++){
    if(hash(k,hit.riverId,side,19349663)<.28)continue;
    const center=(k+.5)*CELL_M+(hash(k,hit.riverId,side,83492791)-.5)*5;
    const halfLength=4+hash(k,hit.riverId,side,73856093)*8.5,along=hit.s-center;
    if(Math.abs(along)>=halfLength)continue;
    const t=along/halfLength,ends=clamp((halfLength-Math.abs(along))/2);
    const shape=hash(k,hit.riverId,side,15485863),bevel=1.5+hash(k,hit.riverId,side,49979687)*.8;
    // Oblique shoulders and an unequal setback break the outline into a few
    // physical planes. The front is never a vertical or sub-metre jump.
    const rawFront=hit.waterHalf*(1.04+.08*shape)+1.0+shape*1.2
      +(1.0+shape*1.2)*Math.abs(t)+(shape-.5)*t;
    const front=Math.max(hit.waterHalf+1.0,Math.min(hit.carveHalf-bevel-1.8,rawFront));
    if(hit.dist<=front)continue;
    const face=clamp((hit.dist-front)/bevel),back=clamp((hit.carveHalf-hit.dist)/1.8);
    const outer=Math.max(-1,Math.min(1,-side*(hit.bankTurn??0)));
    const cap=waterM+1.65+hash(k,hit.riverId,side,67867967)*1.7+outer*.15
      +along*(hash(k,hit.riverId,side,86028121)-.5)*.045;
    const target=Math.min(nativeM,waterM+.08+(cap-waterM-.08)*face);
    const lift=Math.min(2.8,Math.max(0,target-carvedM))*ends*back*dryWeight*source*fallWeight;
    retained=Math.max(retained,lift);
  }
  // Preserve quiet samples byte-for-byte instead of round-tripping through
  // metres. Retention only reduces the already bounded hydrology incision.
  return retained>0?Math.min(nativeH,carvedH+retained/A):carvedH;
}

/** Unequal bedrock shoulders around a fall. These are retained portions of the
 * native bank, shared by rendering and every terrain contact query. Broad caps
 * and oblique fronts replace the uniform scooped profile on dry banks only. */
export function carveWaterfallBankHeight(nativeH:number,carvedH:number,hit:RiverHit,A:number):number {
  const fall=hit.fall;
  if(!fall||!(A>0)||hit.landformsEnabled===false||hit.canyonOwned||nativeH<=carvedH
    ||carvedH<=hit.ws||hit.dist<=hit.waterHalf+.6||hit.dist>=hit.carveHalf)return carvedH;
  const s=hit.s-fall.lip.s,run=fall.end.s-fall.lip.s;
  const ends=clamp((s+9)/4)*clamp((run+14-s)/6);
  if(ends<=0)return carvedH;
  const side=(hit.cross??hit.dist)<0?-1:1,identity=Math.floor(fall.lip.s/10);
  const inner=hit.waterHalf+.6,outer=hit.carveHalf,available=outer-inner;
  if(available<2)return carvedH;
  let retained=0;
  // Two offset, unequal shoulders per bank; no regular staircase or repeated
  // ring around the pool. The cap inclinations and setbacks share one field.
  for(let k=0;k<2;k++){
    const shape=hash(identity+k*7,hit.riverId,side,15485863);
    const center=run*(.15+k*.65)+(shape-.5)*4;
    const half=5+hash(identity+k*7,hit.riverId,side,73856093)*5;
    const along=(s-center)/half,alongGate=clamp((1-Math.abs(along))*half/2.4);
    if(alongGate<=0)continue;
    const front=inner+available*(.08+.16*shape)+Math.abs(along)*.8+along*(shape-.5)*.8;
    const bevel=1.5+.6*shape,face=clamp((hit.dist-front)/bevel),back=clamp((outer-hit.dist)/1.2);
    const cap=fall.end.ws*A+fall.dropM*(.60+.24*shape)+(s-center)*(.035+.035*shape);
    const toe=fall.end.ws*A+.15;
    const target=Math.min(nativeH*A,toe+(cap-toe)*face);
    const lift=Math.min(3.8,Math.max(0,target-carvedH*A),Math.max(0,(carvedH-hit.ws)*A)*.8)*alongGate*back*ends;
    retained=Math.max(retained,lift);
  }
  return retained>0?Math.min(nativeH,carvedH+retained/A):carvedH;
}
